import { useState } from "react";
import { useFirebaseStore } from "../../stores/useFirebaseStore";
import { useAuthStore } from "../../stores/useAuthStore";
import { hasItem } from "../../utils/functions";
import { Movie, MovieOption, MovieOptionsKeyType } from "../../utils/types";
import { logMovieCTA } from "../../service/analytics";

import CancelIcon from "@mui/icons-material/Cancel";
import BookmarkAddIcon from "@mui/icons-material/BookmarkAdd";
import BookmarkRemoveIcon from "@mui/icons-material/BookmarkRemove";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import PushPinIcon from "@mui/icons-material/PushPin";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import ShareIcon from "@mui/icons-material/Share";
import { useLocation } from "react-router-dom";
import { showShare } from "../common/ShareDialog";

export const useMovieItem = (movie: Movie) => {
  const [showOptions, setShowOptions] = useState(false);
  const {
    watchListMovies,
    favoriteMovies,
    addToWatchList,
    addToFavourites,
    removeFromWatchList,
    removeFromFavorite,
    pinMovie,
    unpinMovie,
  } = useFirebaseStore();

  const { profile } = useAuthStore() as any;
  const isLoggedIn = !!profile?.uid;
  const location = useLocation();
  const isWatchListScreen = location.pathname === "/watchlist";

  if (!movie) {
    return {
      showOptions: false,
      getMovieOptions: () => [],
      handleOptionsClick: () => {},
      onOptionClick: () => {},
      closeOptions: () => {},
    };
  }

  const { id } = movie;

  // Find watchlist item and check if pinned (used in both getMovieOptions and return value)
  const watchListItem = watchListMovies.find((item) => item.movieId === id);
  const isPinned = watchListItem?.pinnedAt > 1;

  const handleOptionsClick = () => {
    setShowOptions((p) => !p);
  };

  const getMovieOptions = () => {
    const isBookmark = hasItem(id, watchListMovies);
    const isFavorite = hasItem(id, favoriteMovies);

    let movieOptions: MovieOption[] = [
      {
        type: "share",
        title: "Share",
        icon: <ShareIcon color="action" />,
      },
      {
        type: "cancel",
        title: "Cancel",
        icon: <CancelIcon color="error" />,
      },
    ];

    if (isLoggedIn) {
      const options: MovieOption[] = [
        {
          type: "bookmark",
          title: isBookmark ? "Remove from WatchList" : "Add to WatchList",
          icon: isBookmark ? (
            <BookmarkRemoveIcon color="error" />
          ) : (
            <BookmarkAddIcon color="secondary" />
          ),
        },
        {
          type: "favorite",
          title: isFavorite ? "Remove from Favorites" : "Add to Favorites",
          icon: isFavorite ? (
            <FavoriteIcon color="error" />
          ) : (
            <FavoriteBorderIcon color="error" />
          ),
        },
      ];

      if (isWatchListScreen) {
        options.push({
          type: isPinned ? "unpin" : "pin",
          title: isPinned ? "Unpin Movie" : "Pin Movie",
          icon: isPinned ? (
            <PushPinIcon color="primary" />
          ) : (
            <PushPinOutlinedIcon color="primary" />
          ),
        });
      }

      movieOptions = [...options, ...movieOptions];
    }

    return movieOptions;
  };

  const onOptionClick = (type: MovieOptionsKeyType) => {
    setShowOptions(false);
    switch (type) {
      case "share":
        logMovieCTA("cta_click_share", movie, { share_platform: "dialog" });
        showShare(movie);
        break;
      case "bookmark":
        const isBookmark = hasItem(id, watchListMovies);
        if (isBookmark) {
          logMovieCTA("cta_toggle_watchlist", movie, { action: "remove" });
          removeFromWatchList(id);
          return;
        }
        logMovieCTA("cta_toggle_watchlist", movie, { action: "add" });
        return addToWatchList(movie);
      case "favorite":
        const isFavorite = hasItem(id, favoriteMovies);
        if (isFavorite) {
          logMovieCTA("cta_toggle_favorite", movie, { action: "remove" });
          removeFromFavorite(id);
          return;
        }
        logMovieCTA("cta_toggle_favorite", movie, { action: "add" });
        return addToFavourites(movie);
      case "pin":
        logMovieCTA("cta_toggle_pin", movie, { action: "pin" });
        pinMovie(id);
        break;
      case "unpin":
        logMovieCTA("cta_toggle_pin", movie, { action: "unpin" });
        unpinMovie(id);
        break;
    }
  };

  const closeOptions = () => {
    setShowOptions(false);
  };

  return {
    showOptions,
    getMovieOptions,
    handleOptionsClick,
    onOptionClick,
    closeOptions,
    isPinned,
    isWatchListScreen,
  };
};
